import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, MapPin, ArrowDown } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import { images } from '../mock/mock';
import { menuSections as defSections, menuHours as defHours, menuLegend as defLegend, takeawaySpecials as defTakeaway } from '../mock/menuData';
import { useContent } from '../context/ContentContext';

function PriceRow({ item }) {
  return (
    <div className="py-2.5 border-b border-[#2C3A28]/8 last:border-0">
      <div className="flex items-baseline gap-3">
        <h4 className="font-semibold text-[#2C3A28] tracking-wide uppercase text-sm">
          {item.name}
        </h4>
        {item.tags && (
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[#5A5D5F]">
            {item.tags}
          </span>
        )}
        <span className="flex-1 border-b border-dotted border-[#2C3A28]/25 translate-y-[-3px]" />
        {item.price && (
          <span className="font-semibold text-[#2C3A28] shrink-0">
            ${item.price}
            {item.price2 && <span className="text-[#3B2A20]/50"> / ${item.price2}</span>}
          </span>
        )}
      </div>
      {item.desc && (
        <p className="mt-1 text-[13px] leading-relaxed text-[#2C3A28] max-w-prose">
          {item.desc}
        </p>
      )}
    </div>
  );
}

export default function MenuPage() {
  useReveal([]);
  const { content, txt } = useContent();
  const m = content.menu || {};
  const menuSections = m.sections && m.sections.length ? m.sections : defSections;
  const menuHours = m.hours ?? defHours;
  const menuLegend = m.legend ?? defLegend;
  const takeaway = (m.takeaway && (m.takeaway.items?.length || m.takeaway.bundles?.length)) ? m.takeaway : defTakeaway;
  const takeawayItems = takeaway.items || [];
  const takeawayBundles = takeaway.bundles || [];

  return (
    <div className="bg-[#E9EBED]">
      <div className="pt-28" />
      {/* hero, hidden; remove the spacer above and "hidden" below to reinstate */}
      <section className="hidden relative pt-44 pb-16 min-h-[480px] flex items-center overflow-hidden bg-[#E9E7E2] text-[#2C3A28]">
        <img
          src={images.cafe1}
          alt="Al Naturale cafe"
          className="absolute inset-0 w-full h-full object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#E9E7E2] via-[#E9E7E2]/70 to-[#E9E7E2]/40" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 text-[#2C3A28]">
          <span className="text-[#2C3A28] text-sm font-semibold uppercase tracking-[0.2em]">
            Our menu
          </span>
          <h1
            className="mt-4 text-6xl sm:text-7xl max-w-2xl leading-[1.05]"
            style={{ fontFamily: "'Pinyon Script', cursive" }}
          >
            Breakfast &amp; Lunch
          </h1>
          <p className="mt-5 max-w-xl font-display text-[#3B2A20]/80 text-lg sm:text-xl leading-relaxed">
            {menuHours}. Every coffee made with your choice of our house nut milks.
          </p>
          <a
            href="#full-menu"
            data-testid="view-menu-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#2C3A28] text-[#E9EBED] px-8 py-4 font-semibold hover:bg-[#1f2a1c] transition-colors"
          >
            View Menu <ArrowDown size={18} />
          </a>
        </div>
      </section>

      {/* full text menu (A3 sheet style) */}
      <section id="full-menu" className="py-16 sm:py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center max-w-2xl mx-auto reveal mb-12">
            <h2 className="font-display text-4xl sm:text-5xl font-semibold uppercase text-[#2C3A28] uppercase tracking-tight">
              {txt('menu', 'heading', 'Our Café Menu')}
            </h2>
            <p className="mt-2 text-[#3B2A20]/60 text-sm uppercase tracking-[0.2em]">
              {menuHours}
            </p>
          </div>

          {/* Take Away Meals Specials */}
          {(takeawayItems.length > 0 || takeawayBundles.length > 0) && (
          <div
            className="reveal mb-14 max-w-2xl mx-auto rounded-2xl bg-white/70 border border-[#2C3A28]/8 p-6 sm:p-8"
            data-testid="menu-section-take-away-meals-specials"
          >
            <h3
              className="text-4xl sm:text-5xl text-[#2C3A28] leading-tight text-center"
              style={{ fontFamily: "'Pinyon Script', cursive" }}
            >
              {takeaway.title || 'Take Away Meals Specials'}
            </h3>
            <div className="mt-4 space-y-2.5">
              {takeawayItems.map((item, i) => (
                <div key={`${item.name}-${i}`} className="flex items-baseline gap-3">
                  <h4 className="font-semibold text-[#2C3A28] tracking-wide uppercase text-sm">
                    {item.name}
                  </h4>
                  {item.tags && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-[#5A5D5F]">
                      {item.tags}
                    </span>
                  )}
                </div>
              ))}
            </div>
            {takeawayBundles.length > 0 && (
            <div className="mt-6 grid grid-cols-3 gap-3 text-center">
              {takeawayBundles.map((b, i) => (
                <div key={`${b.qty}-${i}`} className="rounded-xl bg-[#2C3A28] py-4 px-2">
                  <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.18em] text-[#EDEAE0]">{b.qty}</p>
                  <p className="font-display text-3xl sm:text-4xl font-semibold text-[#EDEAE0] leading-tight">${b.price}</p>
                </div>
              ))}
            </div>
            )}
          </div>
          )}


          <div className="mt-12 lg:columns-2 xl:columns-3 gap-10 [column-fill:_balance]">
            {menuSections.map((section) => (
              <div
                key={section.title}
                className="break-inside-avoid mb-10 rounded-2xl bg-white/70 border border-[#2C3A28]/8 p-6 sm:p-7"
                data-testid={`menu-section-${section.title.replace(/[^a-z]+/gi, '-').toLowerCase()}`}
              >
                <h3
                  className="text-4xl sm:text-5xl text-[#2C3A28] leading-tight"
                  style={{ fontFamily: "'Pinyon Script', cursive" }}
                >
                  {section.title}
                </h3>
                {section.note && (
                  <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-[#2C3A28]">
                    {section.note}
                  </p>
                )}
                <div className="mt-4">
                  {section.items.map((item) => (
                    <PriceRow key={item.name} item={item} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-4 text-center text-xs text-[#3B2A20]/55 max-w-3xl mx-auto leading-relaxed">
            {menuLegend}
          </p>

          {/* dine-in card */}
          <div className="reveal mt-14 rounded-3xl bg-[#2C3A28] text-[#E9EBED] p-8 sm:p-10 grid md:grid-cols-[1.4fr_1fr] gap-6 items-center">
            <div>
              <p className="font-display text-3xl text-[#EDEAE0]">Join us for breakfast &amp; lunch</p>
              <p className="mt-3 text-[#E9EBED]/90 leading-relaxed max-w-xl">
                6 Acland Street, St Kilda. Open daily 7am to 3pm, breakfast &amp; lunch only.
                Walk-ins welcome, taste tests always on the house.
              </p>
              <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-[#E9EBED]/85">
                <span className="inline-flex items-center gap-2">
                  <Clock size={17} /> Mon-Sun · 7am to 3pm
                </span>
                <span className="inline-flex items-center gap-2">
                  <MapPin size={17} /> 6 Acland Street, St Kilda
                </span>
              </div>
            </div>
            <div className="flex md:justify-end">
              <Link
                to="/cafe"
                className="inline-flex items-center gap-2 rounded-full bg-[#EDEAE0] text-[#2C3A28] px-8 py-4 font-semibold hover:bg-white transition-colors"
              >
                Visit the café
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
